import { CompanionFace, type CompanionFaceMood } from '@/components/companion/feed/CompanionFace';
import { Box, Flex, IconButton, Text } from '@chakra-ui/react';
import { NavArrowLeft, NavArrowRight } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

/**
 * The dark bar the companion speaks from. It carries the face, the name, where
 * the reader is in the deck, and the only two controls the deck needs.
 */

interface CompanionPanelHeaderProps {
  mood: CompanionFaceMood;
  /** One-based position, or null when there is no deck to walk. */
  index: number | null;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
}

const arrowStyles = {
  variant: 'plain',
  minW: '28px',
  h: '28px',
  borderRadius: '8px',
  color: 'announcement.controlFg',
  _hover: { bg: 'announcement.controlHoverBg' },
  _disabled: { color: 'announcement.controlDisabled', bg: 'transparent', cursor: 'default' },
} as const;

export const CompanionPanelHeader = ({
  mood,
  index,
  total,
  onPrevious,
  onNext,
}: CompanionPanelHeaderProps) => {
  const { t } = useTranslation();
  const hasDeck = index !== null && total > 0;

  return (
    <Flex
      align="center"
      gap="10px"
      pl="16px"
      pr="10px"
      py="11px"
      bg="darkNav"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        bg: 'linear-gradient(115deg, rgba(185, 167, 255, 0.08), transparent 36%), linear-gradient(180deg, rgba(255, 255, 255, 0.035), transparent 62%)',
      }}
    >
      <Box position="relative" zIndex={1} display="flex" alignItems="center" gap="10px" flex={1}>
        <CompanionFace mood={mood} size={22} />
        <Text
          fontFamily="heading"
          fontSize="13.5px"
          fontWeight="700"
          color="announcement.title"
          lineHeight="1"
        >
          {t('companion.panel.title')}
        </Text>
        {hasDeck ? (
          <Text
            fontSize="12.5px"
            fontWeight="500"
            color="announcement.counter"
            data-testid="companion-panel-counter"
          >
            {t('companion.panel.counter', { index, total })}
          </Text>
        ) : null}
      </Box>

      {hasDeck ? (
        <Flex align="center" gap="2px" position="relative" zIndex={1}>
          <IconButton
            {...arrowStyles}
            aria-label={t('companion.panel.previous')}
            disabled={index === 1}
            onClick={onPrevious}
          >
            <NavArrowLeft width={16} height={16} />
          </IconButton>
          <IconButton
            {...arrowStyles}
            aria-label={t('companion.panel.next')}
            disabled={index === total}
            onClick={onNext}
          >
            <NavArrowRight width={16} height={16} />
          </IconButton>
        </Flex>
      ) : null}
    </Flex>
  );
};
